import Keycloak from 'keycloak-js'

import { env } from '@/config/env'

const AUTH_TOKEN_KEY = 'authToken'
const REALM_STORAGE_KEY = 'gim_keycloak_realm'

/** Token value stored when development gate VITE_DEVELOPMENT_GATE_BYPASS_AUTH is enabled. Backend may accept it in dev. */
export const DEVELOPMENT_GATE_BYPASS_AUTH_TOKEN = 'development-gate-bypass-auth'

export function setDevelopmentGateBypassAuthToken(): void {
  localStorage.setItem(AUTH_TOKEN_KEY, DEVELOPMENT_GATE_BYPASS_AUTH_TOKEN)
}
const AUTH_TIMEOUT_MS = 30_000
const TOKEN_REFRESH_BUFFER_SECONDS = 30
/** Floor for the refresh timer so a short-lived or already-stale token can never produce a hot refresh loop. */
const MIN_REFRESH_DELAY_MS = 10_000

/**
 * Returns the current Keycloak realm from the URL query string (?realm=...)
 * or from sessionStorage (set before Keycloak redirect, survives OAuth callback).
 * Read-only — does not modify sessionStorage.
 */
export function getCurrentRealm(): string | null {
  const params = new URLSearchParams(window.location.search)
  const fromUrl = params.get('realm')?.trim() || null
  if (fromUrl) return fromUrl
  try {
    return sessionStorage.getItem(REALM_STORAGE_KEY)?.trim() || null
  } catch {
    return null
  }
}

export function clearCurrentRealm(): void {
  try {
    sessionStorage.removeItem(REALM_STORAGE_KEY)
  } catch {
    /* storage unavailable */
  }
}

/**
 * Resolves the Keycloak realm from the URL query string (?realm=...)
 * or from sessionStorage (persisted before Keycloak redirect so it
 * survives the OAuth callback that strips custom params).
 */
function resolveRealm(): string | null {
  const params = new URLSearchParams(window.location.search)
  const fromUrl = params.get('realm')?.trim() || null

  if (fromUrl) {
    try {
      sessionStorage.setItem(REALM_STORAGE_KEY, fromUrl)
    } catch {
      /* storage unavailable */
    }
    return fromUrl
  }

  try {
    return sessionStorage.getItem(REALM_STORAGE_KEY)?.trim() || null
  } catch {
    return null
  }
}

/**
 * Builds the redirect URI for the OAuth callback.
 * Keycloak does not support query parameters in valid redirect URIs, so we use
 * origin + pathname only. The realm is already persisted in sessionStorage
 * before redirect and survives the callback.
 */
function buildRedirectUri(): string {
  const { origin, pathname } = window.location
  const path = pathname || '/'
  return `${origin}${path}`
}

export class KeycloakAuth {
  private keycloak: Keycloak | null = null
  private initialized = false
  private onTokenChange: (() => void) | null = null
  private refreshTimerId: ReturnType<typeof setTimeout> | null = null

  setTokenChangeListener(listener: (() => void) | null): void {
    this.onTokenChange = listener
  }

  get isAuthenticated(): boolean {
    return this.initialized && !!this.keycloak?.authenticated
  }

  get token(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY)
  }

  /**
   * Initializes Keycloak with Auth Code + PKCE.
   * Rejects if realm is missing, Keycloak config is missing, init fails, or timeout fires.
   * The timeout is properly cleaned up on success.
   */
  async init(): Promise<void> {
    const keycloakUrl = (env.keycloakUrl || '').trim()
    const keycloakClientId = (env.keycloakClientId || '').trim()
    if (!keycloakUrl || !keycloakClientId) {
      throw new Error('KEYCLOAK_CONFIG_MISSING')
    }
    const baseUrl = keycloakUrl.replace(/\/$/, '')

    const realm = resolveRealm()
    if (!realm) {
      throw new Error('MISSING_REALM')
    }

    this.keycloak = new Keycloak({
      url: baseUrl,
      realm,
      clientId: keycloakClientId,
    })

    // Must run before keycloak.init(): keycloak-js arms its internal expiry
    // timer inside setToken (which runs during init) only when onTokenExpired
    // is already assigned. Registering afterwards leaves the first token
    // without an expiry fallback (KPS-377).
    this.registerCallbacks()

    const redirectUri = buildRedirectUri()

    let timeoutId: ReturnType<typeof setTimeout> | undefined

    const timeoutPromise = new Promise<never>((_resolve, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error('AUTH_TIMEOUT'))
      }, AUTH_TIMEOUT_MS)
    })

    try {
      const authenticated = await Promise.race([
        this.keycloak.init({
          onLoad: 'login-required',
          pkceMethod: 'S256',
          checkLoginIframe: false,
          redirectUri,
        }),
        timeoutPromise,
      ])

      if (authenticated && this.keycloak.token) {
        this.persistToken(this.keycloak.token)
        this.scheduleTokenRefresh()
        this.initialized = true
      } else {
        this.clearToken()
        throw new Error('NOT_AUTHENTICATED')
      }
    } finally {
      clearTimeout(timeoutId)
    }
  }

  private persistToken(token: string): void {
    localStorage.setItem(AUTH_TOKEN_KEY, token)
    if (this.initialized && this.onTokenChange) {
      this.onTokenChange()
    }
  }

  private clearToken(): void {
    localStorage.removeItem(AUTH_TOKEN_KEY)
    if (this.initialized && this.onTokenChange) {
      this.onTokenChange()
    }
  }

  /**
   * Proactively refreshes the token TOKEN_REFRESH_BUFFER_SECONDS before it expires.
   * Rescheduled after each refresh so the timer tracks the new token's lifetime.
   * Replaces the reactive onTokenExpired pattern, which fires after expiry and is subject
   * to browser timer throttling on inactive tabs.
   */
  private scheduleTokenRefresh(): void {
    if (this.refreshTimerId !== null) {
      clearTimeout(this.refreshTimerId)
      this.refreshTimerId = null
    }
    const exp = this.keycloak?.tokenParsed?.exp
    if (!exp) return
    const delay = Math.max(
      MIN_REFRESH_DELAY_MS,
      exp * 1000 - TOKEN_REFRESH_BUFFER_SECONDS * 1000 - Date.now(),
    )
    this.refreshTimerId = setTimeout(() => {
      void this.forceTokenRefresh()
    }, delay)
  }

  /**
   * Forces a token refresh (keycloak-js treats minValidity -1 as unconditional)
   * and reschedules the proactive timer.
   *
   * The refresh must be forced: a non-forced updateToken(minValidity) is gated
   * on keycloak-js's own clock-skew arithmetic, which can deem the token "still
   * valid" at the moment our timer fires (client clock ahead of the server, or
   * exact second boundaries) and silently skip the refresh — leaving the token
   * to expire with no further attempts scheduled (KPS-377).
   *
   * Returns true when a valid token is available afterwards; on refresh failure
   * clears the token and redirects to login.
   */
  async forceTokenRefresh(): Promise<boolean> {
    if (!this.keycloak) return false
    try {
      await this.keycloak.updateToken(-1)
    } catch {
      this.clearToken()
      this.keycloak.login()
      return false
    }
    if (!this.keycloak.token) {
      return false
    }
    this.persistToken(this.keycloak.token)
    this.scheduleTokenRefresh()
    return true
  }

  logout(): void {
    if (this.refreshTimerId !== null) {
      clearTimeout(this.refreshTimerId)
      this.refreshTimerId = null
    }
    this.onTokenChange = null
    this.clearToken()
    try {
      sessionStorage.removeItem(REALM_STORAGE_KEY)
    } catch {
      /* storage unavailable */
    }
    if (this.keycloak) {
      this.keycloak.logout()
    } else {
      window.location.reload()
    }
  }

  private registerCallbacks(): void {
    if (!this.keycloak) return

    // Fallback for cases where the proactive timer fires late (e.g. suspended tab).
    this.keycloak.onTokenExpired = () => {
      void this.forceTokenRefresh()
    }

    this.keycloak.onAuthRefreshSuccess = () => {
      if (this.keycloak?.token) {
        this.persistToken(this.keycloak.token)
      }
    }

    this.keycloak.onAuthLogout = () => {
      if (this.refreshTimerId !== null) {
        clearTimeout(this.refreshTimerId)
        this.refreshTimerId = null
      }
      this.clearToken()
      try {
        sessionStorage.removeItem(REALM_STORAGE_KEY)
      } catch {
        /* storage unavailable */
      }
    }
  }
}

/** App-wide singleton shared by AuthProvider and the API layer. */
export const keycloakAuth = new KeycloakAuth()
